import type { Schedule } from "@innoclouds/api";
import { useNow } from "@vueuse/core";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { defineStore } from "pinia";
import { computed, h, ref } from "vue";
import Icon from "#/assets/iconsDynamic/ScheduleNotification.vue";
import { useNotifications } from "#/ui/notifications";
import { parseOffsetMinutes, parseTimeToMinutes } from "#/utils/datetime";
import { getDayOfWeek, isNightAt, isScheduleVisibleAt } from "#/utils/workSchedule";

dayjs.extend(utc);
dayjs.extend(timezone);

const atMinutes = (day: dayjs.Dayjs, minutes: number) =>
	day
		.hour(Math.floor(minutes / 60))
		.minute(minutes % 60)
		.second(0)
		.millisecond(0);

const pushWindowOpen = (targets: dayjs.Dayjs[], day: dayjs.Dayjs, open: string | null | undefined, close: string | null | undefined) => {
	const openMinutes = parseTimeToMinutes(open ?? undefined);
	const closeMinutes = parseTimeToMinutes(close ?? undefined);
	if (openMinutes === null || closeMinutes === null || openMinutes === closeMinutes) return;
	targets.push(atMinutes(day, openMinutes));
};

const SCHEDULE_TOAST_ID = `schedule`;

export const useScheduleStore = defineStore("schedule", () => {
	const notifications = useNotifications();
	const now = useNow({ immediate: true, interval: 10_000 });
	const raw = ref<Schedule | null>(null);

	const branchNow = computed(() => (raw.value ? dayjs(now.value).utc().utcOffset(timezoneOffset.value) : dayjs(now.value)));

	const schedule = computed(() => {
		const disclaimerText = raw.value?.close_time_disclaimer_text;
		if (!raw.value?.schedule_by_day?.length) return { isClosed: false, nextOpenTime: null, disclaimerText, todayItem: null, isNight: false };

		const todayItem = raw.value.schedule_by_day.find((item) => item.day_of_week === getDayOfWeek(branchNow.value));
		const isNight = isNightAt(raw.value.schedule_by_day, branchNow.value);
		const isOpen = isScheduleVisibleAt(raw.value.schedule_by_day, branchNow.value);

		if (isOpen) return { isClosed: false, nextOpenTime: null, disclaimerText, todayItem, isNight };

		const startOfToday = branchNow.value.startOf("day");
		let nextOpenAt: dayjs.Dayjs | null = null;

		for (let dayOffset = 0; dayOffset <= 7; dayOffset++) {
			const candidateDay = startOfToday.add(dayOffset, "day");
			const candidateItem = raw.value?.schedule_by_day?.find((item) => item.day_of_week === getDayOfWeek(candidateDay));
			if (!candidateItem || candidateItem.is_weekend) continue;

			const candidateOpens: dayjs.Dayjs[] = [];
			pushWindowOpen(candidateOpens, candidateDay, candidateItem.open_time, candidateItem.close_time);
			pushWindowOpen(candidateOpens, candidateDay, candidateItem.night?.am_night_open_time, candidateItem.night?.am_night_close_time);
			pushWindowOpen(candidateOpens, candidateDay, candidateItem.night?.pm_night_open_time, candidateItem.night?.pm_night_close_time);

			const upcoming = candidateOpens.filter((openAt) => branchNow.value.isBefore(openAt)).sort((a, b) => a.valueOf() - b.valueOf());
			const soonest = upcoming[0];
			if (!soonest) continue;

			nextOpenAt = soonest;
			break;
		}

		return { isClosed: true, nextOpenTime: nextOpenAt, disclaimerText, todayItem, isNight };
	});

	const isClosed = computed(() => !!schedule.value?.isClosed);
	const nextOpenTime = computed(() => schedule.value?.nextOpenTime ?? null);
	const disclaimerText = computed(() => schedule.value?.disclaimerText ?? "");
	const today = computed(() => schedule.value?.todayItem);
	const isNight = computed(() => !!schedule.value?.isNight);
	const hashSum = computed(() => raw.value?.hash_sum ?? "");
	const timezoneOffset = computed(() => parseOffsetMinutes(raw.value?.timezone ?? ""));

	const set = (schedule: Schedule) => {
		raw.value = schedule;
	};

	const notify = () => {
		notifications.toast(disclaimerText.value, {
			id: SCHEDULE_TOAST_ID,
			duration: Infinity,
			closeButton: false,
			action: { label: "✕", onClick: () => notifications.dismiss(SCHEDULE_TOAST_ID) },
			icon: h(Icon),
			style: { background: "var(--accentColor)", color: "white", borderColor: "var(--accentColor)" },
			classes: { icon: "schedule_notification_icon" },
			actionButtonStyle: {
				background: "var(--accentColor)",
				color: "white",
				borderRadius: "50%",
				width: "24px",
				height: "24px",
				display: "flex",
				alignItems: "center",
				lineHeight: "1",
				fontSize: "1.4rem",
			},
		});
	};

	return {
		set,
		notify,
		hashSum,
		branchNow,
		SCHEDULE_TOAST_ID,
		isClosed,
		isNight,
		nextOpenTime,
		timezoneOffset,
		disclaimerText,
		today,
	};
});
